<template>
  <div class="theme-matching-confirm-view">
    <div class="theme-matching-confirm-content">
      <!-- 猫からのメッセージ -->
      <ChatMessage
        :message="$t('themeMatching.confirm.chatMessage')"
        :cat-icon-size="'medium'"
        :use-writer-type="true"
        class="theme-matching-confirm-content__message"
      />

      <!-- 企画項目の記入状況（ジャンル・カテゴリを上部に表示） -->
      <div class="theme-matching-confirm-content__fields">
        <p class="fields-heading">{{ $t("themeMatching.confirm.referenceInfoHeading") }}</p>
        <p
          v-for="field in novelInfoStatuses"
          :key="field.name"
          class="field-label"
          :class="{ 'field-label--empty': !field.filled }"
        >
          {{ field.label }}
        </p>
        <p
          v-for="field in fieldStatuses"
          :key="field.name"
          class="field-label"
          :class="{ 'field-label--empty': !field.filled }"
        >
          {{ field.label }}
        </p>
      </div>

      <!-- Fish消費量表示 -->
      <div class="theme-matching-confirm-content__fish-info">
        <i18n path="themeMatching.confirm.fishCostLabel" tag="div" class="fish-info-content fish-info-label">
          <span place="amount" class="fish-info-amount"
            >{{ formattedFishCost }} {{ $t("themeMatching.confirm.fishUnit") }}</span
          >
        </i18n>

        <!-- Fish不足警告 -->
        <div v-if="isFishInsufficient" class="fish-warning">
          <div class="fish-warning-icon">⚠️</div>
          <div class="fish-warning-content">
            <p class="fish-warning-message">{{ $t("themeMatching.confirm.fishInsufficient") }}</p>
            <i18n path="themeMatching.confirm.fishShortageDetail" tag="p" class="fish-warning-detail">
              <span place="amount" class="fish-shortage-amount">{{ formattedFishShortage }}</span>
            </i18n>
          </div>
        </div>
      </div>

      <!-- 依頼ボタン -->
      <div class="theme-matching-confirm-content__actions">
        <button
          class="theme-matching-confirm-content__request-button"
          :class="{
            'theme-matching-confirm-content__request-button--insufficient':
              isFishInsufficient && !isFanFictionWork && !isScriptModeWork,
          }"
          :disabled="isRequestButtonDisabled"
          @click="handleRequest"
        >
          {{ requestButtonText }}
        </button>
        <button class="theme-matching-confirm-content__back-button" type="button" @click="handleBack">
          {{ $t("themeMatching.confirm.backButton") }}
        </button>
      </div>
    </div>

    <!-- 注意文言 -->
    <div class="theme-matching-confirm-content__notice">
      <p v-if="isFanFictionWork" class="notice-text">{{ $t("themeMatching.validation.fanFictionDisabled") }}</p>
      <p v-if="isScriptModeWork" class="notice-text">{{ $t("themeMatching.validation.scriptModeDisabled") }}</p>
      <p class="notice-text">{{ $t("themeMatching.confirm.noticeSavedContent") }}</p>
      <p class="notice-text">{{ $t("themeMatching.confirm.noticeNoLearning") }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import { AssistClient } from "@/lib/clients";
import { logError, logInfo } from "@/lib/utils/logger";
import { writerTypeToAssistWriterType, ClientPlatform, AssistWriterType } from "@/lib/models/assist";
import { useThemeStore } from "@/stores/theme";
import { usePageEditStore } from "@/stores/pageEdit";
import { showNotifyDialog } from "@/lib/dialog";
import { AssistAnalytics } from "@/lib/utils/analytics/assist";
import { ASSIST_REQUEST_TITLES } from "@/constants/assistMessages";
import { ThemeMatchingAnalytics } from "@/lib/utils/analytics/theme";
import { isFanFictionNovel, isScriptModeNovel } from "@/composables/useThemeMatchingValidation";
import {
  THEME_FIELD_NAMES,
  MATCHING_THEME_REQUIRED_FIELDS,
  THEME_FIELD_LABEL_KEYS,
  MATCHING_THEME_FISH,
} from "@/constants/themeMatching";
import type { Novel } from "@/lib/models";

interface FieldStatus {
  name: string;
  label: string;
  filled: boolean;
  required: boolean;
}

interface NovelInfoStatus {
  name: string;
  label: string;
  filled: boolean;
}

const assistClient = new AssistClient();

const platform = ClientPlatform.WEB;

export default Vue.extend({
  name: "ThemeMatchingConfirmContent",
  components: {
    ChatMessage,
  },
  props: {
    novelId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      requestProcessing: false,
      analytics: null as AssistAnalytics | null,
      themeMatchingAnalytics: null as ThemeMatchingAnalytics | null,
      matchingThemeFish: null as number | null,
    };
  },
  computed: {
    // 保存済みの theme データ（Pinia。バリデーション通過時に取得済み）
    themeDetails(): { name: string; value: string }[] {
      const themeStore = useThemeStore();
      return themeStore.theme?.detail ?? [];
    },

    novel(): Novel | undefined {
      if (!this.novelId) return undefined;
      return this.$store.getters["novelModule/novel"](this.novelId) as Novel | undefined;
    },

    /** 一覧上部: タイトル・ジャンル・カテゴリ（ラベルのみ） */
    novelInfoStatuses(): NovelInfoStatus[] {
      const hasTitle = !!this.novel?.title && !!this.novel.title.trim();
      const hasGenre = !!this.novel?.genre;
      const hasCategory = !!this.novel?.category && this.novel.category.length > 0;

      return [
        {
          name: "title",
          label: this.$t("novelDetail.title") as string,
          filled: hasTitle,
        },
        {
          name: "genre",
          label: this.$t("homePage.novelList.filter.genre") as string,
          filled: hasGenre,
        },
        {
          name: "category",
          label: this.$t("homePage.novelList.filter.category") as string,
          filled: hasCategory,
        },
      ];
    },

    fieldStatuses(): FieldStatus[] {
      return THEME_FIELD_NAMES.map((name) => {
        const value = this.themeDetails.find((detail) => detail.name === name)?.value;
        return {
          name,
          label: this.$t(THEME_FIELD_LABEL_KEYS[name]) as string,
          filled: !!value && !!value.trim(),
          required: MATCHING_THEME_REQUIRED_FIELDS.includes(name),
        };
      });
    },

    // 記入済み項目の value 合計文字数（アナリティクス用）
    contentLength(): number {
      return this.themeDetails.reduce((sum, detail) => {
        if (!detail.value || !detail.value.trim()) return sum;
        return sum + detail.value.length;
      }, 0);
    },

    // Fish 消費量（一律。API 取得値優先、失敗時は定数フォールバック）
    fishCost(): number {
      return this.matchingThemeFish ?? MATCHING_THEME_FISH;
    },
    formattedFishCost(): string {
      return this.fishCost.toLocaleString("ja-JP");
    },

    // Fish残高チェック
    isFishInsufficient(): boolean {
      const canConsumeFn = this.canConsumeFish;
      if (typeof canConsumeFn === "function") {
        return !canConsumeFn(this.fishCost);
      }
      return this.currentFish < this.fishCost;
    },
    isFanFictionWork(): boolean {
      return isFanFictionNovel(this.novel);
    },
    isScriptModeWork(): boolean {
      return isScriptModeNovel(this.novel);
    },
    fishShortage(): number {
      if (!this.isFishInsufficient) return 0;
      return this.fishCost - this.currentFish;
    },
    formattedFishShortage(): string {
      return this.fishShortage.toLocaleString("ja-JP");
    },

    isRequestButtonDisabled(): boolean {
      return (
        !this.novelId ||
        this.isFishInsufficient ||
        this.requestProcessing ||
        this.isFanFictionWork ||
        this.isScriptModeWork
      );
    },
    requestButtonText(): string {
      if (this.requestProcessing) {
        return this.$t("themeMatching.confirm.requestLoading") as string;
      }
      if (this.isFishInsufficient) {
        return this.$t("themeMatching.confirm.fishInsufficient") as string;
      }
      return this.$t("themeMatching.confirm.requestButton") as string;
    },

    canConsumeFish() {
      return this.$store.getters["fishModule/canConsumeFish"];
    },
    currentFish() {
      return this.$store.getters["fishModule/currentFish"];
    },
    ...mapGetters("writerTypeResultModule", ["writerType"]),
  },
  async mounted() {
    // Fish データの初期化を確実に実行
    this.$store.dispatch("fishModule/fetchFishData");

    // アナリティクス初期化
    this.analytics = new AssistAnalytics(this.$gtm);
    if (this.$gtm) {
      this.themeMatchingAnalytics = new ThemeMatchingAnalytics(this.$gtm);
    }

    try {
      this.matchingThemeFish = await assistClient.getMatchingThemeFish();
    } catch (error) {
      logError(
        { module: "ThemeMatchingConfirmContent", action: "mounted" },
        "getMatchingThemeFish の取得に失敗したため定数を使用",
        error
      );
      this.matchingThemeFish = MATCHING_THEME_FISH;
    }
  },
  methods: {
    ...mapActions("fishModule", ["consumeFishForRequest"]),

    handleBack(): void {
      this.$store.dispatch("assistNavigationModule/showRequestContent");
    },

    async handleRequest(): Promise<void> {
      if (this.isRequestButtonDisabled) return;

      logInfo({ module: "ThemeMatchingConfirmContent", action: "handleRequest" }, "企画マッチング依頼処理開始");

      // マッチング対象は保存済みデータのため、未保存の変更がある場合は先に保存を促す
      const pageEditStore = usePageEditStore();
      if (pageEditStore.isModified) {
        await showNotifyDialog({
          title: this.$t("themeMatching.validation.unsavedTitle") as string,
          content: this.$t("themeMatching.validation.unsavedMessage") as string,
        });
        return;
      }

      this.requestProcessing = true;

      try {
        // 1. Fish消費実行（クライアント側減算）
        const consumeSuccess = await this.consumeFishForRequest(this.fishCost);
        if (!consumeSuccess) {
          await this.$store.dispatch(
            "assistNavigationModule/setError",
            this.$t("themeMatching.confirm.errors.fishConsumeFailed")
          );
          return;
        }

        // 2. アシスタント猫の選択状態を確認（優先）。未選択時は作家タイプ診断の結果を使用
        const selectedCatType = this.$store.getters["assistantCatModule/selectedType"];

        let assistWriterType: AssistWriterType;
        let analyticsWriterType: string;

        if (selectedCatType) {
          assistWriterType = selectedCatType;
          analyticsWriterType = assistWriterType;
        } else {
          assistWriterType = writerTypeToAssistWriterType(this.writerType);
          const isRandomWriterType = this.$store.getters["writerTypeResultModule/isRandom"];
          analyticsWriterType = isRandomWriterType ? "RANDOM" : assistWriterType;
        }

        // 3. 企画マッチング依頼（requestTitle は BE 保存用にロケール非依存）
        const requestTitle = ASSIST_REQUEST_TITLES.MATCHING_THEME;
        await assistClient.createThemeMatching({
          novelId: this.novelId,
          writerType: assistWriterType,
          requestTitle,
          platform,
        });

        // 4. アナリティクス: 依頼イベント送信（assist_request + theme_matching_request）
        if (this.analytics) {
          this.analytics.trackRequestSubmit(
            analyticsWriterType,
            undefined,
            this.contentLength,
            this.novelId,
            "themeMatching"
          );
        }
        this.themeMatchingAnalytics?.trackRequest({
          novelId: this.novelId,
          writerType: analyticsWriterType,
          fishSpent: this.fishCost,
          contentLength: this.contentLength,
        });

        // 5. 成功時: 履歴画面への遷移
        await this.$store.dispatch("assistNavigationModule/showHistory");
      } catch (error: any) {
        logError(
          { module: "ThemeMatchingConfirmContent", action: "handleRequest" },
          "企画マッチング依頼でエラー",
          error
        );

        // エラーの種類に応じたメッセージ設定（ManuscriptSelectContent と同一パターン）
        let errorMessage = this.$t("themeMatching.confirm.errors.unexpected") as string;
        if (error.message) {
          if (error.message.includes("Fish") || error.message.includes("残高")) {
            errorMessage = this.$t("themeMatching.confirm.errors.fishInsufficient") as string;
          } else if (error.message.includes("GPT Handler") || error.message.includes("Lambda")) {
            errorMessage = this.$t("themeMatching.confirm.errors.aiProcessingFailed") as string;
          } else if (error.message.includes("401") || error.message.includes("認証")) {
            errorMessage = this.$t("themeMatching.confirm.errors.authRequired") as string;
          } else if (
            error.message.includes("二次創作") ||
            error.message.includes("fan fiction") ||
            error.message.includes("Fan fiction")
          ) {
            errorMessage = this.$t("themeMatching.validation.fanFictionDisabled") as string;
          } else if (error.message.includes("ネットワーク") || error.message.includes("network")) {
            errorMessage = this.$t("themeMatching.confirm.errors.network") as string;
          } else {
            errorMessage = error.message;
          }
        }

        // クライアント側で減算した Fish を戻すため残高を再取得
        await this.$store.dispatch("fishModule/fetchFishData");
        await this.$store.dispatch("assistNavigationModule/setError", errorMessage);
      } finally {
        this.requestProcessing = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.theme-matching-confirm-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
}

.theme-matching-confirm-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
}

.theme-matching-confirm-content__message {
  margin-bottom: 8px;
}

.theme-matching-confirm-content__fields {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-sm);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fields-heading {
  margin: 0 0 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.field-label {
  margin: 0;
  padding-left: 12px;
  position: relative;
  font-size: var(--font-size-xs);
  color: var(--color-text-normal);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &::before {
    content: "";
    position: absolute;
    left: 2px;
    top: 50%;
    width: 3px;
    height: 3px;
    border-radius: var(--radius-rounded);
    background-color: currentColor;
    opacity: 0.45;
    transform: translateY(-50%);
  }

  &--empty {
    color: var(--color-text-low);
    text-decoration: line-through;
  }
}

.theme-matching-confirm-content__fish-info {
  text-align: center;
  padding: 8px 0;
}

.fish-info-content {
  font-size: var(--font-size-xs);
  line-height: 1.5;
}

.fish-info-label {
  color: var(--color-text-low);
}

.fish-info-amount {
  color: var(--color-text-normal);
  font-weight: var(--font-weight-bold);
}

.fish-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 12px;
  background-color: var(--color-warning-bg);
  border: 1px solid var(--color-warning-border);
  border-radius: var(--radius-xs);
}

.fish-warning-icon {
  font-size: var(--font-size-md);
  flex-shrink: 0;
  margin-top: 2px;
}

.fish-warning-content {
  flex: 1;
  text-align: left;
}

.fish-warning-message {
  margin: 0 0 4px 0;
  color: var(--color-warning-text);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  line-height: 1.3;
}

.fish-warning-detail {
  margin: 0;
  color: var(--color-warning-text);
  font-size: var(--font-size-xs);
  line-height: 1.3;
}

.fish-shortage-amount {
  color: var(--color-warning-text);
  font-weight: var(--font-weight-bold);
}

.theme-matching-confirm-content__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.theme-matching-confirm-content__request-button {
  position: relative;
  min-width: 200px;
  max-width: 240px;
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  border: none;
  border-radius: var(--radius-rounded);
  padding: 16px 24px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: all 0.2s ease;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    sans-serif;
  margin: 0 auto;
  display: block;

  @include hover-overlay;

  &:active:not(:disabled) {
    filter: brightness(0.85);
    transform: translateY(1px);
  }

  &:disabled {
    background-color: var(--color-surface-base);
    color: var(--color-inactive);
    cursor: not-allowed;

    &::after {
      display: none;
    }
  }

  &--insufficient {
    background-color: var(--color-warning);

    &:active:not(:disabled) {
      filter: brightness(0.85);
    }
  }

  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
}

.theme-matching-confirm-content__back-button {
  background: none;
  border: none;
  color: var(--color-text-low);
  font-size: var(--font-size-xs);
  cursor: pointer;
  padding: 4px 8px;

  &:hover {
    opacity: 0.7;
  }
}

.theme-matching-confirm-content__notice {
  margin-top: auto;
  padding-top: 16px;
  text-align: left;
}

.notice-text {
  margin: 0 0 4px 0;
  color: var(--color-inactive);
  font-size: var(--font-size-xxs);
  line-height: 1.4;
  font-weight: var(--font-weight-regular);

  &:last-child {
    margin-bottom: 0;
  }
}

/* レスポンシブ対応 */
@media (max-width: 576px) {
  .theme-matching-confirm-content {
    gap: 12px;
  }

  .theme-matching-confirm-content__request-button {
    padding: 12px 16px;
    font-size: var(--font-size-xs);
    width: 100%;
  }

  .theme-matching-confirm-content__notice {
    padding: 10px 0;
  }

  .notice-text {
    font-size: 9px;
    line-height: 1.3;
  }
}
</style>
